import React, { useState, useEffect, useCallback, useRef } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Trash2, Save, Loader2, GitBranch, GripVertical, Upload } from "lucide-react";

export default function FlowsPage() {
  const [flows, setFlows] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingStep, setUploadingStep] = useState(null);
  const stepFileRefs = useRef({});

  const fetchData = useCallback(async () => {
    try {
      const [flowsRes, mediaRes] = await Promise.all([
        authAxios.get(`${API}/flows`),
        authAxios.get(`${API}/media`).catch(() => ({ data: [] }))
      ]);
      setFlows(flowsRes.data);
      setMedia(mediaRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startNew = () => {
    setEditing({
      name: "", trigger_keyword: "", any_message_trigger: false, is_active: true, enable_loop: false,
      steps: [{ id: `s_${Date.now()}`, step_order: 1, title: "Step 1", response_type: "text", response_content: "", media_url: "", media_asset_id: null, next_step_id: null, is_terminal: false, options: [] }]
    });
  };

  const startEdit = async (flow) => {
    try { const { data } = await authAxios.get(`${API}/flows/${flow.id}`); setEditing(data); }
    catch { setEditing(flow); }
  };

  const handleSave = async () => {
    if (!editing.name) return;
    setSaving(true);
    try {
      if (editing.id) { await authAxios.put(`${API}/flows/${editing.id}`, editing); }
      else { await authAxios.post(`${API}/flows`, editing); }
      setEditing(null); fetchData();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (flowId) => {
    if (!window.confirm("Delete this flow?")) return;
    try { await authAxios.delete(`${API}/flows/${flowId}`); fetchData(); }
    catch (err) { console.error(err); }
  };

  const addStep = () => {
    if (!editing) return;
    const order = editing.steps.length + 1;
    setEditing({
      ...editing,
      steps: [...editing.steps, { id: `s_${Date.now()}`, step_order: order, title: `Step ${order}`, response_type: "text", response_content: "", media_url: "", media_asset_id: null, next_step_id: null, is_terminal: false, options: [] }]
    });
  };

  const updateStep = (idx, field, value) => {
    const steps = [...editing.steps];
    steps[idx] = { ...steps[idx], [field]: value };
    setEditing({ ...editing, steps });
  };

  const removeStep = (idx) => {
    const steps = editing.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_order: i + 1 }));
    setEditing({ ...editing, steps });
  };

  const addOption = (idx) => {
    const steps = [...editing.steps];
    steps[idx] = { ...steps[idx], options: [...(steps[idx].options || []), { id: `o_${Date.now()}`, label: "", value: "", next_step_id: null }] };
    setEditing({ ...editing, steps });
  };

  const updateOption = (si, oi, field, value) => {
    const steps = [...editing.steps];
    const opts = [...(steps[si].options || [])];
    opts[oi] = { ...opts[oi], [field]: value };
    steps[si] = { ...steps[si], options: opts };
    setEditing({ ...editing, steps });
  };

  const removeOption = (si, oi) => {
    const steps = [...editing.steps];
    steps[si] = { ...steps[si], options: steps[si].options.filter((_, j) => j !== oi) };
    setEditing({ ...editing, steps });
  };

  const uploadStepMedia = async (stepIdx, file) => {
    if (!file) return;
    setUploadingStep(stepIdx);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await authAxios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      updateStep(stepIdx, "media_url", data.url);
    } catch (err) { alert(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingStep(null); }
  };

  if (loading) return <div className="p-8"><div className="animate-pulse h-64 bg-slate-200 rounded-xl" /></div>;

  return (
    <div className="p-8" data-testid="flows-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0f172a] flex items-center gap-2"><GitBranch className="w-6 h-6" /> Reply Flows</h1>
          <p className="text-sm text-muted-foreground mt-1">Create step-based auto-reply flows with unlimited steps and branching</p>
        </div>
        <Button onClick={startNew} className="bg-[#0f172a] hover:bg-[#1e293b]" data-testid="create-flow-btn"><Plus className="w-4 h-4 mr-2" /> New Flow</Button>
      </div>

      {flows.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><GitBranch className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No flows yet. Create one to get started.</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {flows.map((flow) => (
            <Card key={flow.id} data-testid={`flow-${flow.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-sm font-medium">{flow.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {flow.any_message_trigger ? "Any message" : `Trigger: ${flow.trigger_keyword}`} &middot; {flow.steps?.length || 0} steps {flow.enable_loop ? "&middot; Loop" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={flow.is_active ? "bg-[#22c55e] text-white" : "bg-slate-200 text-slate-600"}>{flow.is_active ? "Active" : "Inactive"}</Badge>
                  <Button variant="outline" size="sm" onClick={() => startEdit(flow)} data-testid={`edit-flow-${flow.id}`}>Edit</Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => handleDelete(flow.id)} data-testid={`delete-flow-${flow.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Flow Editor Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="flow-editor">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Flow" : "New Flow"}</DialogTitle></DialogHeader>
          {editing && (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4 pb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Flow Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Welcome Flow" data-testid="flow-name-input" /></div>
                  <div className="space-y-2"><Label>Trigger Keyword</Label><Input value={editing.trigger_keyword} onChange={(e) => setEditing({ ...editing, trigger_keyword: e.target.value })} placeholder="hello" disabled={editing.any_message_trigger} data-testid="flow-trigger-input" /></div>
                </div>
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-2"><Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} className="data-[state=checked]:bg-[#22c55e]" data-testid="flow-active-toggle" /><Label>Active</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={editing.any_message_trigger} onCheckedChange={(v) => setEditing({ ...editing, any_message_trigger: v })} /><Label>Any Message Trigger</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={editing.enable_loop} onCheckedChange={(v) => setEditing({ ...editing, enable_loop: v })} data-testid="flow-loop-toggle" /><Label>Enable Loop</Label></div>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-medium">Steps ({editing.steps.length})</Label>
                    <Button size="sm" variant="outline" onClick={addStep} data-testid="add-step-btn"><Plus className="w-3 h-3 mr-1" /> Add Step</Button>
                  </div>
                  <div className="space-y-3">
                    {editing.steps.map((step, idx) => (
                      <Card key={step.id || idx} className="bg-slate-50">
                        <CardContent className="p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2"><GripVertical className="w-4 h-4 text-slate-400" /><span className="text-xs font-medium text-slate-500">Step {idx + 1}</span><Badge variant="outline" className="text-[10px]">{step.response_type}</Badge></div>
                            {editing.steps.length > 1 && <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeStep(idx)}><Trash2 className="w-3 h-3" /></Button>}
                          </div>
                          <Input value={step.title} onChange={(e) => updateStep(idx, "title", e.target.value)} placeholder="Step title" className="text-sm" />
                          <div className="grid grid-cols-2 gap-2">
                            <select value={step.response_type} onChange={(e) => updateStep(idx, "response_type", e.target.value)} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm">
                              <option value="text">Text</option><option value="image">Image</option><option value="audio">Audio</option><option value="video">Video</option><option value="document">Document/PDF</option><option value="quick_reply">Quick Reply Options</option>
                            </select>
                            <select value={step.next_step_id || ""} onChange={(e) => updateStep(idx, "next_step_id", e.target.value || null)} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm">
                              <option value="">No next step (end)</option>
                              {editing.steps.filter((_, i) => i !== idx).map((s) => <option key={s.id} value={s.id}>Step {editing.steps.indexOf(s) + 1}: {s.title}</option>)}
                            </select>
                          </div>
                          {step.response_type === "text" || step.response_type === "quick_reply" ? (
                            <Textarea value={step.response_content} onChange={(e) => updateStep(idx, "response_content", e.target.value)} placeholder="Reply text..." rows={2} className="text-sm" />
                          ) : (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <Input value={step.media_url} onChange={(e) => updateStep(idx, "media_url", e.target.value)} placeholder="Media URL or upload..." className="text-sm flex-1" />
                                <Button
                                  size="sm" variant="outline"
                                  onClick={() => {
                                    if (!stepFileRefs.current[idx]) stepFileRefs.current[idx] = document.createElement('input');
                                    const inp = stepFileRefs.current[idx];
                                    inp.type = 'file';
                                    inp.accept = 'image/*,audio/*,video/*,.pdf,.doc,.docx';
                                    inp.onchange = (e) => e.target.files?.[0] && uploadStepMedia(idx, e.target.files[0]);
                                    inp.click();
                                  }}
                                  disabled={uploadingStep === idx}
                                  data-testid={`upload-step-${idx}`}
                                >
                                  {uploadingStep === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                </Button>
                              </div>
                              {media.length > 0 && (
                                <select value={step.media_asset_id || ""} onChange={(e) => { const m = media.find(x => x.id === e.target.value); if(m) { updateStep(idx, "media_url", m.file_url); updateStep(idx, "media_asset_id", m.id); } }} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm w-full">
                                  <option value="">Select from Media Library</option>
                                  {media.filter(m => m.file_type === step.response_type || step.response_type === "document").map(m => <option key={m.id} value={m.id}>{m.name} ({m.file_type})</option>)}
                                </select>
                              )}
                            </div>
                          )}
                          {/* Quick Reply Options */}
                          {step.response_type === "quick_reply" && (
                            <div className="space-y-2 pl-4 border-l-2 border-[#22c55e]">
                              <div className="flex items-center justify-between"><Label className="text-xs font-medium">Quick Reply Options</Label><Button size="sm" variant="outline" onClick={() => addOption(idx)} className="h-6 text-xs"><Plus className="w-3 h-3 mr-1" />Option</Button></div>
                              {(step.options || []).map((opt, oi) => (
                                <div key={opt.id || oi} className="grid grid-cols-4 gap-2">
                                  <Input value={opt.label} onChange={(e) => updateOption(idx, oi, "label", e.target.value)} placeholder="Label" className="text-xs" />
                                  <Input value={opt.value} onChange={(e) => updateOption(idx, oi, "value", e.target.value)} placeholder="Value" className="text-xs" />
                                  <select value={opt.next_step_id || ""} onChange={(e) => updateOption(idx, oi, "next_step_id", e.target.value || null)} className="h-9 rounded-md border border-input bg-transparent px-2 text-xs"><option value="">No next</option>{editing.steps.map((s,si) => <option key={s.id} value={s.id}>Step {si+1}</option>)}</select>
                                  <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500" onClick={() => removeOption(idx, oi)}><Trash2 className="w-3 h-3" /></Button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-2"><Switch checked={step.is_terminal} onCheckedChange={(v) => updateStep(idx, "is_terminal", v)} /><Label className="text-xs">Terminal (stop here)</Label></div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0f172a] hover:bg-[#1e293b]" data-testid="save-flow-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{saving ? "Saving..." : "Save Flow"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
